import { useCallback, useEffect, useState } from "react";

import styles from "./BulkActions.module.css";
import { useBetaDataTableContext as useTableContext } from "@zapier/design-system";
import { BulkActionsCheckbox } from "./BulkActionsCheckbox";
import { BulkActionsDropdown } from "./BulkActionsDropdown";
import { BulkActionsBar } from "./BulkActionsBar";

export interface BulkSelectActionDisabledState {
  disabled: boolean;
  tooltipText?: string;
}

export interface BulkSelectAction {
  disabled?: boolean;
  /** Resolves the disabled state for the live selection; disables the action if either it or `disabled` says so. */
  getDisabledState?: (selected: string[]) => BulkSelectActionDisabledState;
  icon: string;
  id?: string;
  isVisible?: (selected: string[]) => boolean;
  /** Called when the selection changes while the action is visible. */
  onSelectionChange?: (selected: string[]) => void;
  onClick: (selected: string[]) => void;
  text: string;
  tooltipText?: string;
}

interface Props<E> {
  assetTypeLabel: string;
  selectableEntities?: E[];
  bulkSelectActions?: BulkSelectAction[];
  showSelectPaused?: boolean;
  emptyStateMessage?: string;
}

function BulkActions<E>({
  assetTypeLabel,
  selectableEntities,
  bulkSelectActions,
  showSelectPaused,
  emptyStateMessage,
}: Props<E>) {
  const selectableCount = selectableEntities?.length || 0;

  const {
    clearSelection: clearSelectedEntities,
    contextMenuCoords,
    selectedRows,
    setContextMenuCoords,
    setSelectedRows,
  } = useTableContext();

  const selectAllEntities = useCallback(() => {
    const allEntities =
      selectableEntities?.reduce((accumulator: string[], entity: E) => {
        accumulator.push((entity as any).id);
        return accumulator;
      }, []) || [];
    setSelectedRows(allEntities);
  }, [setSelectedRows, selectableEntities]);

  const [isBulkSelectOpen, setIsBulkSelectOpen] = useState<boolean>(false);

  useEffect(() => {
    // If there are new context menu coordinates, we set the bulk select to open.
    // Otherwise defer to the current bulk select visibility status, that can be set
    // explicitly by the bulk selection button onclicks
    setIsBulkSelectOpen((isBulkSelectOpen) =>
      contextMenuCoords ? true : isBulkSelectOpen,
    );
  }, [contextMenuCoords]);

  const closeDropdown = () => {
    setIsBulkSelectOpen(false);
    setContextMenuCoords(null);
  };

  return (
    <>
      <div className={styles.bulkEdit}>
        <div className={styles.buttonSpace}>
          <BulkActionsCheckbox
            assetTypeLabel={assetTypeLabel}
            closeDropdown={closeDropdown}
            clearSelectedEntities={clearSelectedEntities}
            selectAllEntities={selectAllEntities}
            selectedCount={selectedRows.length}
            selectableCount={selectableCount}
          />
          {showSelectPaused && (
            <BulkActionsDropdown<E>
              assetTypeLabel={assetTypeLabel}
              contextMenuCoords={contextMenuCoords}
              selectedCount={selectedRows.length}
              isBulkSelectOpen={isBulkSelectOpen}
              setIsBulkSelectOpen={setIsBulkSelectOpen}
              selectableCount={selectableCount}
              selectAllEntities={selectAllEntities}
              selectableEntities={selectableEntities}
              setSelectedRows={setSelectedRows}
              clearSelectedEntities={clearSelectedEntities}
              closeDropdown={closeDropdown}
              showSelectPaused={showSelectPaused}
            />
          )}
        </div>
        {selectedRows.length > 0 && (
          <BulkActionsBar
            assetTypeLabel={assetTypeLabel}
            selectedRows={selectedRows}
            clearSelectedEntities={clearSelectedEntities}
            bulkSelectActions={bulkSelectActions}
            emptyStateMessage={emptyStateMessage}
          />
        )}
      </div>
    </>
  );
}

export default BulkActions;
